import { useCallback, useEffect, useRef, useState } from "react";
import {
  type WaveBlock,
  type WaveCommentRecord,
  type WaveDocEditor,
  type WaveGalleryItem,
  type WaveParticipantRecord,
} from "@vida/wave-core";
import {
  inferBlobKind,
  normalizedContentType,
  registerWaveBlobAttachment,
  signedDownloadURL,
  uploadBlobFile,
} from "../../services/waveBlobUpload";
import { resolveGalleryBlockIdForMutation } from "../../services/waveGalleryUpload";
import { WaveGalleryViewer } from "./WaveGalleryViewer";

type EditHandlers = {
  onFocus?: () => void;
  onBlur?: () => void;
  onTypingActivity?: () => void;
};

type Props = {
  waveId: string;
  block: WaveBlock;
  editor?: WaveDocEditor | null;
  readOnly?: boolean;
  remoteContentRevision?: number;
  isCoalescedFeedGroup?: boolean;
  hideAddButton?: boolean;
  registerOpenPicker?: (open: () => void) => void;
  comments?: WaveCommentRecord[];
  participants?: WaveParticipantRecord[];
  mentionParticipants?: WaveParticipantRecord[];
  currentUserSub?: string;
  edit: EditHandlers;
  onAfterEdit?: () => void;
  onUploadError?: (message: string) => void;
  onPostGalleryComment?: (
    body: string,
    parentId: string | null,
    galleryItemId: string,
  ) => void;
  onToggleReaction?: (commentId: string, emoji: string) => void;
};

function isImageItem(item: WaveGalleryItem): boolean {
  const mime = (item.mimeType ?? "").toLowerCase();
  const name = (item.fileName ?? "").toLowerCase();
  return mime.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif|svg)$/.test(name);
}

function GalleryThumbnail({
  waveId,
  item,
  remoteContentRevision,
  onOpen,
}: {
  waveId: string;
  item: WaveGalleryItem;
  remoteContentRevision: number;
  onOpen: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const status = item.uploadStatus ?? "uploading";

  useEffect(() => {
    if (!item.s3Key || status !== "ready") {
      setUrl(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const signed = await signedDownloadURL(item.s3Key!, waveId, true, item.fileName);
        if (!cancelled) setUrl(signed);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.s3Key, item.fileName, status, waveId, remoteContentRevision]);

  return (
    <button
      type="button"
      className="wave-gallery-thumb"
      onClick={onOpen}
      disabled={status !== "ready" || !item.s3Key}
      aria-label={item.fileName ?? "Immagine galleria"}
    >
      {status === "uploading" && <span className="wave-gallery-thumb-placeholder">…</span>}
      {status === "error" && <span className="wave-gallery-thumb-placeholder error">!</span>}
      {status === "ready" && url && (
        <img
          src={url}
          alt={item.caption ?? item.fileName ?? ""}
          loading="lazy"
          decoding="async"
        />
      )}
      {status === "ready" && !url && (
        <span className="wave-gallery-thumb-placeholder">?</span>
      )}
    </button>
  );
}

export function WaveGalleryBlock({
  waveId,
  block,
  editor,
  readOnly,
  remoteContentRevision = 0,
  isCoalescedFeedGroup = false,
  hideAddButton = false,
  registerOpenPicker,
  comments = [],
  participants = [],
  mentionParticipants,
  currentUserSub = "",
  edit,
  onAfterEdit,
  onUploadError,
  onPostGalleryComment,
  onToggleReaction,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState(block.payload.title ?? "Galleria");
  const [draftDescription, setDraftDescription] = useState(block.payload.text ?? "");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [busy, setBusy] = useState(false);

  const items = (block.payload.galleryItems ?? []).filter(isImageItem);
  const gridLayoutClass =
    items.length === 0 ? "empty" : items.length === 1 ? "count-1" : "masonry";
  useEffect(() => {
    if (!isEditingTitle) {
      setDraftTitle(block.payload.title ?? "Galleria");
    }
  }, [block.payload.title, remoteContentRevision, isEditingTitle]);

  useEffect(() => {
    if (!isEditingDescription) {
      setDraftDescription(block.payload.text ?? "");
    }
  }, [block.payload.text, remoteContentRevision, isEditingDescription]);

  useEffect(() => {
    registerOpenPicker?.(() => fileInputRef.current?.click());
  }, [registerOpenPicker, readOnly, busy, editor]);

  const syncAfter = useCallback(() => {
    onAfterEdit?.();
  }, [onAfterEdit]);

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!editor || readOnly) return;
      const list = Array.from(files).filter((f) => inferBlobKind(f) === "image");
      if (!list.length) return;
      setBusy(true);
      const targetId = resolveGalleryBlockIdForMutation(editor, editor.blocks, block.id);
      for (const file of list) {
        const kind = inferBlobKind(file);
        const mime = normalizedContentType(file, kind);
        const item = editor.addGalleryItem(targetId, {
          fileName: file.name,
          mimeType: mime,
          fileSize: file.size,
          uploadStatus: "uploading",
        });
        try {
          const result = await uploadBlobFile(file);
          editor.setGalleryItemMeta(targetId, item.id, {
            s3Key: result.s3Key,
            mimeType: mime,
            fileName: file.name,
            fileSize: file.size,
            uploadStatus: "ready",
          });
          try {
            await registerWaveBlobAttachment(waveId, result.s3Key, file.name);
          } catch {
            /* optional */
          }
        } catch (e) {
          editor.setGalleryItemMeta(targetId, item.id, { uploadStatus: "error" });
          onUploadError?.(e instanceof Error ? e.message : "Upload fallito");
        }
      }
      syncAfter();
      setBusy(false);
    },
    [block.id, editor, onUploadError, readOnly, syncAfter, waveId],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (readOnly || !e.dataTransfer.files?.length) return;
      void uploadFiles(e.dataTransfer.files);
    },
    [readOnly, uploadFiles],
  );

  const removeItem = (itemId: string) => {
    if (!editor || readOnly) return;
    editor.removeGalleryItem(block.id, itemId);
    syncAfter();
    if (lightboxIndex != null) setLightboxIndex(null);
  };

  const commitTitle = (title: string) => {
    if (!editor) return;
    editor.setText(block.id, "title", title);
    syncAfter();
  };

  const commitDescription = (text: string) => {
    if (!editor) return;
    editor.setText(block.id, "text", text);
    syncAfter();
  };

  const canEditMeta = !readOnly && !isCoalescedFeedGroup && editor;

  return (
    <div
      className="wave-gallery-block"
      onDragOver={(e) => {
        if (readOnly) return;
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={onDrop}
    >
      <div className="wave-gallery-header">
        <input
          className="wave-title"
          value={draftTitle}
          readOnly={!canEditMeta}
          placeholder="Galleria"
          onFocus={() => {
            setIsEditingTitle(true);
            edit.onFocus?.();
          }}
          onBlur={() => {
            setIsEditingTitle(false);
            edit.onBlur?.();
            commitTitle(draftTitle);
          }}
          onChange={(e) => {
            edit.onTypingActivity?.();
            setDraftTitle(e.target.value);
            commitTitle(e.target.value);
          }}
        />
        {!readOnly && !isCoalescedFeedGroup && !hideAddButton && (
          <button
            type="button"
            className="secondary-btn wave-gallery-add"
            disabled={busy || !editor}
            onClick={() => fileInputRef.current?.click()}
          >
            + Immagine
          </button>
        )}
      </div>

      {(canEditMeta || draftDescription.trim().length > 0) && (
        <textarea
          className="wave-gallery-description"
          value={draftDescription}
          readOnly={!canEditMeta}
          placeholder="Descrizione (opzionale)"
          rows={2}
          onFocus={() => {
            setIsEditingDescription(true);
            edit.onFocus?.();
          }}
          onBlur={() => {
            setIsEditingDescription(false);
            edit.onBlur?.();
            commitDescription(draftDescription);
          }}
          onChange={(e) => {
            edit.onTypingActivity?.();
            setDraftDescription(e.target.value);
            commitDescription(e.target.value);
          }}
        />
      )}

      <div className={`wave-gallery-grid ${gridLayoutClass}`} role="list">
        {items.length === 0 && (
          <p className="muted wave-gallery-empty">
            Trascina qui una o più immagini
            {!hideAddButton ? ", oppure usa «+ Immagine»." : ", oppure «Aggiungi immagine» nel menu ⋯."}
          </p>
        )}
        {items.map((item, index) => (
          <div
            key={`${item.id}-${item.s3Key ?? ""}-${item.uploadStatus ?? ""}`}
            className="wave-gallery-cell"
            role="listitem"
          >
            <GalleryThumbnail
              waveId={waveId}
              item={item}
              remoteContentRevision={remoteContentRevision}
              onOpen={() => setLightboxIndex(index)}
            />
            {!readOnly && !isCoalescedFeedGroup && editor && (
              <button
                type="button"
                className="wave-gallery-remove"
                aria-label="Rimuovi immagine"
                onClick={() => removeItem(item.id)}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) void uploadFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {lightboxIndex != null && items[lightboxIndex]?.s3Key && (
        <WaveGalleryViewer
          waveId={waveId}
          blockId={block.id}
          items={items}
          initialIndex={lightboxIndex}
          remoteContentRevision={remoteContentRevision}
          comments={comments}
          participants={participants}
          mentionParticipants={mentionParticipants ?? participants}
          currentUserSub={currentUserSub}
          readOnly={readOnly || !onPostGalleryComment}
          onClose={() => setLightboxIndex(null)}
          onPostComment={(body, parentId, galleryItemId) =>
            onPostGalleryComment?.(body, parentId, galleryItemId)
          }
          onToggleReaction={(commentId, emoji) =>
            onToggleReaction?.(commentId, emoji)
          }
        />
      )}
    </div>
  );
}
